import { useState, forwardRef, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useTheme } from 'styled-components';
import Icon from '../Icon/Icon';
import { IconKeys } from '../Icon/IconKeys';
import { ThemeType } from '../../../theme/themeType';
import { Button } from '../../ui/Button/Button';
import { getFormattedDate } from '../../../utils/dateUtils';
import 'react-datepicker/dist/react-datepicker.css';
import S from './DatePickerStyles';
import useGetFleet from '../../../API/hooks/useGetFleetDetails';
import useGetFleetId from '../../../API/hooks/useGetFleetId';

type DateType = Date | null;
type Props = {
  isActive?: boolean;
  onSubmit: (data: { startDate: Date, endDate: DateType }) => void;
  initialData?: {
    startDate: DateType;
    endDate: DateType;
  };
  // onChanged: (startDate: Date, endDate: Date) => void;
};

const DatePickerButton = forwardRef(({ isActive, onClick, setOpen }: any, ref: any) => (
  <Button
    ref={ref}
    height="26px"
    width="105px"
    borderRadius="100px"
    gap="10px"
    active={isActive}
    onClick={() => {
      onClick();
      setOpen();
    }}
  >
    <Icon iconKey={IconKeys.DATE} />
    <p>Custom</p>
  </Button>
));

function DatePicker({ onSubmit, isActive, initialData }: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const [isOpen, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<DateType>(initialData?.startDate || null);
  const [endDate, setEndDate] = useState<DateType>(initialData?.endDate || null);
  const fleetId = useGetFleetId();
  const { data: fleetData } = useGetFleet(fleetId);

  const onChange = (dates: [DateType, DateType]): void => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    if (isActive === false) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [isActive]);

  const handleApplyClick = (): void => {
    if (!startDate) return;
    onSubmit({
      startDate,
      endDate: endDate || startDate,
    });
    setOpen(false);
  };

  return (
    <S.Wrapper>
      <ReactDatePicker
        monthsShown={2}
        fixedHeight
        open={isOpen}
        showPopperArrow={false}
        shouldCloseOnSelect={false}
        selected={startDate}
        onCalendarOpen={() => setOpen(true)}
        onCalendarClose={() => setOpen(false)}
        onChange={onChange}
        startDate={startDate}
        endDate={endDate}
        calendarStartDay={fleetData?.settings?.weekStartDay ?? 1}
        customInput={<DatePickerButton isActive={isActive} setOpen={() => setOpen(true)} />}
        selectsRange
        disabledKeyboardNavigation
      >
        <div className="react-datepicker__wrapper">
          <div className="react-datepicker__footer">
            <div className="label">
              {startDate ? getFormattedDate(startDate?.toString()) : ''}
              {endDate ? ` - ${getFormattedDate(endDate?.toString())}` : ''}
            </div>
            <div className="actions">
              <Button
                width="77px"
                height="34px"
                color={theme.colors.text}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                width="117px"
                height="34px"
                title="Apply"
                textColor={theme.colors.black}
                background={theme.colors.main_cta}
                hoverBackground={theme.colors.white}
                hoverColor={theme.colors.black}
                onClick={handleApplyClick}
                disabled={!startDate}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </ReactDatePicker>
    </S.Wrapper>
  );
}

export default DatePicker;
